import { Close, Email, Shuffle, TrendingUp } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import LoadingButton from "../../../components/LoadingButton";
import {
  INBOX_TYPE_IMAP,
  INSTANTLY_DEFAULT_WARMUP_TAG,
} from "../../../constants";
import { hostname, parseError } from "../../../utils";

interface InboxWarmupDialogProps {
  open: boolean;
  onClose: () => void;
  getToken: () => Promise<string>;
  companyId: string;
  eventListener: (event: any) => void;
}

interface WarmupInbox {
  id: string;
  name: string;
  domain: string;
  email: string;
  warmupEnabled: boolean;
  warmupAccountId: string | null;
  warmupFilterTag: string | null;
  created_date: string;
  inboxType: string;
}

interface WarmupStats {
  sent: number;
  landed_inbox: number;
  landed_spam?: number;
  received: number;
  health_score: number;
  health_score_label: string;
}

const generateRandomTag = (): string => {
  const adjectives = [
    "golden",
    "silver",
    "cosmic",
    "electric",
    "mighty",
    "swift",
    "brave",
    "clever",
    "happy",
    "lucky",
    "solar",
    "lunar",
    "stellar",
    "arctic",
    "tropical",
  ];
  const nouns = [
    "pineapple",
    "thunder",
    "phoenix",
    "dragon",
    "falcon",
    "tiger",
    "dolphin",
    "rocket",
    "comet",
    "crystal",
    "diamond",
    "sunrise",
    "galaxy",
    "mountain",
    "ocean",
  ];
  const adjective = adjectives[Math.floor(Math.random() * adjectives.length)];
  const noun = nouns[Math.floor(Math.random() * nouns.length)];
  const number = Math.floor(Math.random() * 100);
  return `${adjective}-${noun}-${number}`;
};

const InboxWarmupDialog: React.FC<InboxWarmupDialogProps> = ({
  open,
  onClose,
  getToken,
  companyId,
  eventListener,
}) => {
  const [inboxes, setInboxes] = useState<WarmupInbox[]>([]);
  const [loading, setLoading] = useState(false);
  const [enablingInbox, setEnablingInbox] = useState<string | null>(null);
  const [analytics, setAnalytics] = useState<Record<string, WarmupStats>>({});
  const [filterTag, setFilterTag] = useState<string>("");
  const [savingFilterTag, setSavingFilterTag] = useState(false);
  const [selectedInboxIds, setSelectedInboxIds] = useState<Set<string>>(
    new Set()
  );

  const loadWarmupInboxesWithLoading = async () => {
    setLoading(true);
    await loadWarmupInboxes();
    setLoading(false);
  };

  const loadWarmupInboxes = async () => {
    try {
      const token = await getToken();
      const response = await axios.get(`${hostname}/inboxes/warmup/list`, {
        params: { companyId },
        headers: { Authorization: `Bearer ${token}` },
      });
      const inboxesList = response.data.inboxes || [];
      setInboxes(inboxesList);
      setSelectedInboxIds(new Set());

      // Load analytics for enabled warmup inboxes
      const enabledEmails = inboxesList
        .filter((inbox: WarmupInbox) => inbox.warmupEnabled)
        .map(
          (inbox: WarmupInbox) =>
            inbox.email || inbox.warmupAccountId || inbox.domain
        )
        .filter(
          (email: string | null) => email && email.includes("@")
        ) as string[];

      if (enabledEmails.length > 0) {
        loadWarmupAnalytics(enabledEmails);
      }
    } catch (error) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(error),
      });
    }
  };

  const loadWarmupAnalytics = async (emails: string[]) => {
    try {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/inboxes/warmup/analytics`,
        { companyId, emails },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setAnalytics(response.data.aggregate_data || {});
    } catch (error) {
      console.error("Failed to load warmup analytics:", error);
    }
  };

  const handleEnableWarmup = async (inboxId: string) => {
    setEnablingInbox(inboxId);
    try {
      const token = await getToken();
      await axios.post(
        `${hostname}/inboxes/${inboxId}/warmup`,
        { companyId },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      eventListener({
        type: "SET_SUCCESS",
        message: "Warmup enabled successfully!",
      });
      await loadWarmupInboxes();
    } catch (error) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(error),
      });
    } finally {
      setEnablingInbox(null);
    }
  };

  const handleDisableWarmup = async (inboxId: string) => {
    setInboxes((prevInboxes) =>
      prevInboxes.map((inbox) =>
        inbox.id === inboxId ? { ...inbox, warmupEnabled: false } : inbox
      )
    );

    try {
      const token = await getToken();
      await axios.delete(`${hostname}/inboxes/${inboxId}/warmup`, {
        params: { companyId },
        headers: { Authorization: `Bearer ${token}` },
      });

      eventListener({
        type: "SET_SUCCESS",
        message: "Warmup disabled successfully!",
      });
    } catch (error) {
      setInboxes((prevInboxes) =>
        prevInboxes.map((inbox) =>
          inbox.id === inboxId ? { ...inbox, warmupEnabled: true } : inbox
        )
      );

      eventListener({
        type: "SET_ERROR",
        error: parseError(error),
      });
    }
  };

  const handleSaveFilterTag = async () => {
    if (selectedInboxIds.size === 0) {
      eventListener({
        type: "SET_ERROR",
        error: "Please select at least one inbox to update.",
      });
      return;
    }

    setSavingFilterTag(true);
    try {
      const token = await getToken();
      await axios.post(
        `${hostname}/inboxes/warmup/filter-tag`,
        {
          companyId,
          filterTag: filterTag.trim(),
          inboxIds: Array.from(selectedInboxIds),
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      eventListener({
        type: "SET_SUCCESS",
        message: `Filter tag updated for ${selectedInboxIds.size} inbox(es)!`,
      });

      // Update local state
      setInboxes((prevInboxes) =>
        prevInboxes.map((inbox) =>
          selectedInboxIds.has(inbox.id)
            ? { ...inbox, warmupFilterTag: filterTag.trim() || null }
            : inbox
        )
      );
      setSelectedInboxIds(new Set());
      setFilterTag("");
    } catch (error) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(error),
      });
    } finally {
      setSavingFilterTag(false);
    }
  };

  const handleGenerateRandomTag = () => {
    setFilterTag(generateRandomTag());
  };

  const handleToggleInbox = (inboxId: string) => {
    setSelectedInboxIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(inboxId)) {
        newSet.delete(inboxId);
      } else {
        newSet.add(inboxId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedInboxIds.size === inboxes.length) {
      setSelectedInboxIds(new Set());
    } else {
      setSelectedInboxIds(new Set(inboxes.map((inbox) => inbox.id)));
    }
  };

  useEffect(() => {
    if (open && companyId) {
      loadWarmupInboxesWithLoading();
      setFilterTag("");
      setSelectedInboxIds(new Set());
    }
  }, [open, companyId]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: "60vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <TrendingUp />
          <Typography variant="h6">Inbox Warmup</Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Enable warmup for your IMAP inboxes to improve email deliverability.
            Warmup gradually increases your sending volume to build sender
            reputation.
          </Typography>
        </Box>

        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Credit Cost:</strong> Inbox warming costs 20 credits per
            month per inbox. You will be charged on the first day of each month
            for all inboxes with warming enabled.
          </Typography>
        </Alert>

        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Why does warming take 2 weeks?</strong>
          </Typography>
          <Typography variant="body2">
            Email warming is a gradual process that builds trust with email
            providers over time. Starting with just a few emails per day and
            slowly increasing volume over 2 weeks allows mailbox providers
            (Gmail, Outlook, etc.) to recognize your domain as a legitimate
            sender. This methodical approach significantly improves
            deliverability and ensures your emails land in the inbox rather than
            spam, establishing a strong sender reputation that protects your
            long-term email performance.
          </Typography>
        </Alert>

        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Filtering Warmup Emails</strong>
          </Typography>
          <Typography variant="body2">
            All warmup emails contain a filter tag in the subject or body. You
            can create email filters or rules in your inbox to automatically
            move these warmup messages to a separate folder. Select inboxes
            below and set a custom filter tag.
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2 }}>
            <TextField
              size="small"
              label="Custom Filter Tag"
              placeholder={INSTANTLY_DEFAULT_WARMUP_TAG}
              value={filterTag}
              onChange={(e) => setFilterTag(e.target.value)}
              sx={{ minWidth: 250 }}
              InputProps={{
                endAdornment: (
                  <Tooltip title="Generate random tag">
                    <IconButton
                      onClick={handleGenerateRandomTag}
                      edge="end"
                      size="small"
                    >
                      <Shuffle fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ),
              }}
            />
            <LoadingButton
              variant="outlined"
              size="small"
              onClick={handleSaveFilterTag}
              loading={savingFilterTag}
              disabled={selectedInboxIds.size === 0}
            >
              Apply to Selected ({selectedInboxIds.size})
            </LoadingButton>
          </Box>
        </Alert>

        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "200px",
            }}
          >
            <Typography>Loading inboxes...</Typography>
          </Box>
        ) : inboxes.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "200px",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Email sx={{ fontSize: 64, color: "text.secondary" }} />
            <Typography color="text.secondary">No inboxes found.</Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={
                        selectedInboxIds.size === inboxes.length &&
                        inboxes.length > 0
                      }
                      indeterminate={
                        selectedInboxIds.size > 0 &&
                        selectedInboxIds.size < inboxes.length
                      }
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Filter Tag</TableCell>
                  <TableCell align="center">Type</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Health Score</TableCell>
                  <TableCell align="center">Sent</TableCell>
                  <TableCell align="center">Inbox</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {inboxes.map((inbox) => {
                  const emailKey =
                    inbox.email ||
                    inbox.warmupAccountId ||
                    inbox.domain ||
                    "";
                  const stats = analytics[emailKey];

                  const displayStats = {
                    sent: stats?.sent ?? 0,
                    landed_inbox: stats?.landed_inbox ?? 0,
                    received: stats?.received ?? 0,
                    health_score: stats?.health_score ?? 0,
                    health_score_label: stats?.health_score_label || "0%",
                  };

                  return (
                    <TableRow
                      key={inbox.id}
                      selected={selectedInboxIds.has(inbox.id)}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedInboxIds.has(inbox.id)}
                          onChange={() => handleToggleInbox(inbox.id)}
                        />
                      </TableCell>
                      <TableCell>{inbox.name}</TableCell>
                      <TableCell>
                        {inbox.email || inbox.warmupAccountId || inbox.domain}
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "monospace",
                            color: inbox.warmupFilterTag
                              ? "text.primary"
                              : "text.secondary",
                          }}
                        >
                          {inbox.warmupFilterTag || INSTANTLY_DEFAULT_WARMUP_TAG}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={inbox.inboxType?.toUpperCase() || "N/A"}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">
                        {inbox.warmupEnabled ? (
                          <Chip
                            label="Active"
                            color="primary"
                            size="small"
                            icon={<TrendingUp />}
                          />
                        ) : inbox.inboxType === INBOX_TYPE_IMAP ? (
                          <Chip
                            label="Disabled"
                            variant="outlined"
                            size="small"
                          />
                        ) : (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontStyle: "italic" }}
                          >
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight:
                              inbox.warmupEnabled && stats ? "bold" : "normal",
                            color:
                              inbox.warmupEnabled && stats
                                ? "primary.main"
                                : "text.primary",
                          }}
                        >
                          {inbox.warmupEnabled
                            ? displayStats.health_score_label
                            : "—"}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">
                          {inbox.warmupEnabled ? displayStats.sent : "—"}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">
                          {inbox.warmupEnabled ? displayStats.landed_inbox : "—"}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {inbox.inboxType === INBOX_TYPE_IMAP ? (
                          inbox.warmupEnabled ? (
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              onClick={() => handleDisableWarmup(inbox.id)}
                            >
                              Disable
                            </Button>
                          ) : (
                            <LoadingButton
                              variant="contained"
                              color="primary"
                              size="small"
                              onClick={() => handleEnableWarmup(inbox.id)}
                              loading={enablingInbox === inbox.id}
                            >
                              Enable
                            </LoadingButton>
                          )
                        ) : (
                          <Tooltip title="Only IMAP inboxes support warmup">
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontStyle: "italic" }}
                            >
                              N/A
                            </Typography>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default InboxWarmupDialog;
